import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';

/**
 * 좌측 프리셋 목록 + 프리셋 단위 조작 버튼
 *
 * - 프로젝트 기본: 개인 설정이 없는 모든 사용자에게 적용되는 프리셋 (권한 2 필요)
 * - 내 기본: 내 계정에만 적용되는 프리셋 (권한 1로 가능)
 */
const PresetList = ({
  presets,
  selectedPresetId,
  myDefaultPresetId,
  canEdit,
  onSelect,
  onAdd,
  onClone,
  onEdit,
  onDelete,
  onSetProjectDefault,
  onSetMyDefault,
}) => {
  const selected = presets.find((p) => p.presetId === selectedPresetId) || null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAdd} disabled={!canEdit}>
          새 프리셋
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={onClone}
          disabled={!canEdit || !selected}
        >
          복제
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onEdit}
          disabled={!canEdit || !selected}
        >
          이름
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          disabled={!canEdit || !selected || selected.isDefault === 1 || selected.isLocked === 1}
        >
          삭제
        </Button>
      </Stack>

      <Divider />

      <List sx={{ flexGrow: 1, overflowY: 'auto', py: 0 }}>
        {presets.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              프리셋이 없습니다. "새 프리셋"으로 추가하세요.
            </Typography>
          </Box>
        )}

        {presets.map((preset) => (
          <ListItemButton
            key={preset.presetId}
            selected={preset.presetId === selectedPresetId}
            onClick={() => onSelect(preset.presetId)}
            sx={{ alignItems: 'flex-start', flexDirection: 'column', gap: 0.5, py: 2 }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {preset.presetName}
              </Typography>
              {preset.isDefault === 1 && (
                <Tooltip title="개인 설정이 없는 사용자에게 적용됩니다">
                  <Chip size="small" color="primary" icon={<StarIcon />} label="프로젝트 기본" />
                </Tooltip>
              )}
              {preset.presetId === myDefaultPresetId && (
                <Chip size="small" color="success" icon={<PersonIcon />} label="내 기본" />
              )}
              {preset.isLocked === 1 && <Chip size="small" variant="outlined" label="잠김" />}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              카메라 {preset.cameraCount} · 씬 {preset.sceneCount} · 단계 {preset.stepCount}
            </Typography>

            {preset.description && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {preset.description}
              </Typography>
            )}
          </ListItemButton>
        ))}
      </List>

      <Divider />

      <Stack spacing={1} sx={{ p: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<StarIcon />}
          onClick={onSetProjectDefault}
          disabled={!canEdit || !selected || selected.isDefault === 1}
        >
          프로젝트 기본으로 지정
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<PersonIcon />}
          onClick={() => onSetMyDefault(selected && selected.presetId === myDefaultPresetId ? null : selectedPresetId)}
          disabled={!selected}
        >
          {selected && selected.presetId === myDefaultPresetId ? '내 기본 해제' : '내 기본으로 지정'}
        </Button>
      </Stack>
    </Box>
  );
};

export default PresetList;
